import React, { useCallback, useEffect, useMemo, useState, useRef, useContext } from "react";
import Button from "@material-ui/core/Button";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import Toolbar from "@material-ui/core/Toolbar";
import Divider from "@material-ui/core/Divider";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Authorize from "../components/Authorize";
import { permissionProfiles } from "../components/Authorize/permissionProfiles";
import { isAuthorized } from "components/Authorize/authorizeUtil";
import { TenantFeatureFlag } from "features/saasManagement/TenantConfig/TenantFeatureFlag";
import { useGetClientsWithConfigsQuery } from "../features/clients/clientConfigSlice";
import { AppContext } from "../App";
import {
    selectIsAdmin,
    selectIsClient,
    selectIsLoggedIn,
    selectIsOwner,
    selectIsSaasManager,
} from "../features/user/userSlice";

import {
    IconLogout,
} from "../icons";

import { sideNavStyles } from "./style";
import { buildMenu, findActiveMenuNode } from "./menuConfig";

export default function SideNav() {
    const classes = sideNavStyles();
    const history = useHistory();
    const location = useLocation();
    const { setPageTitle } = useContext(AppContext);
    const isAdmin = useSelector(selectIsAdmin);
    const isOwner = useSelector(selectIsOwner);
    const isClient = useSelector(selectIsClient);
    const isSaasManager = useSelector(selectIsSaasManager);
    const isUserLoaded = useSelector(selectIsLoggedIn);
    const railRef = useRef(null);
    const panelRef = useRef(null);

    let { data: clientsWithConfigs } = useGetClientsWithConfigsQuery(null, {
        skip: isAdmin || !isUserLoaded,
    });
    clientsWithConfigs = clientsWithConfigs || [];

    const hasMechanicalInvoicePermission = isAuthorized(
        permissionProfiles.INVOICE.READ_MECHANICAL_INVOICE
    );
    const hasStorageInvoicePermission = isAuthorized(
        permissionProfiles.INVOICE.READ_STORAGE_INVOICE
    );
    const hasTransInvoicePermission = isAuthorized(
        permissionProfiles.INVOICE.READ_TRANSPORTATION_INVOICE
    );

    const invoiceUrl = useMemo(() => {
        if (hasTransInvoicePermission) return "invoices/loads";
        if (hasMechanicalInvoicePermission) return "invoices/activity";
        if (hasStorageInvoicePermission) return "invoices/storage";
        return undefined;
    }, [hasTransInvoicePermission, hasMechanicalInvoicePermission, hasStorageInvoicePermission]);

    const isUsingTransports = useMemo(() => {
        if (!isUserLoaded) return false;
        if (!isClient) return true;
        return clientsWithConfigs?.length
            ? clientsWithConfigs.some((c) => c?.config?.transportConfig?.useTransport)
            : false;
    }, [isUserLoaded, isClient, clientsWithConfigs]);

    const isUsingWashes = useMemo(() => {
        if (!isUserLoaded) return false;
        if (!isClient) return true;
        return clientsWithConfigs?.length
            ? clientsWithConfigs.some((c) => c?.config?.washConfig?.useWashProgram)
            : false;
    }, [isUserLoaded, isClient, clientsWithConfigs]);

    const showUsers = useMemo(() => {
        if (!isUserLoaded) return false;
        return !!isOwner;
    }, [isUserLoaded, isOwner]);

    const [activeTopKey, setActiveTopKey] = useState(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [openMap, setOpenMap] = useState({});

    const toggleOpen = useCallback((key) => {
        setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);
    const isOpen = useCallback((key) => !!openMap[key], [openMap]);

    const logout = () => history.push("/logout");

    const go = useCallback((link) => {
        if (!link) return;

        history.push(`/${link}`);
        setPanelOpen(false);
    }, [history]);

    const topItems = useMemo(() => buildMenu({
        isSaasManager,
        isUsingTransports,
        isUsingWashes,
        invoiceUrl,
        showUsers,
    }), [
        isSaasManager,
        isUsingTransports,
        isUsingWashes,
        invoiceUrl,
        showUsers
    ]);

    useEffect(() => {
        const activeNode = findActiveMenuNode(
            topItems,
            location.pathname,
            location.search
        );
        if (activeNode) {
            var activeChild = activeNode?.children?.find((child) => location?.pathname?.includes(child?.link));
            setPageTitle(activeChild?.text || activeNode.text);
        }
        else {
            //root level is the dashboard
            setPageTitle("Dashboard");
        }
    }, [location.pathname, location.search, topItems]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (!panelOpen) return;

            const clickedInsidePanel = panelRef.current?.contains(event.target);
            const clickedInsideRail = railRef.current?.contains(event.target);

            if (!clickedInsidePanel && !clickedInsideRail) {
                setPanelOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [panelOpen]);

    useEffect(() => {
        const currentPath = location?.pathname || "/";
        const matchesNode = (node) => {
            if (!node) return false;

            if (node.key === "dashboard") {
                return (
                    currentPath === "/" ||
                    currentPath === "/dashboard" ||
                    currentPath.startsWith("/dashboard/")
                );
            }

            if (node.link && typeof node.link === "string") {
                const base = `/${node.link.split("?")[0]}`;
                if (currentPath === base || currentPath.startsWith(base + "/")) {
                    return true;
                }
            }

            if (node.children?.length) return node.children.some(matchesNode);

            return false;
        };

        const found = topItems.find((t) => matchesNode(t));
        if (found) {
            setActiveTopKey(found.key);
            setPanelOpen(false);
        }
    }, [location.pathname, topItems]);

    const onRailClick = (item) => {
        if (item.checkVisibility && !item.checkVisibility()) return;

        setActiveTopKey(item.key);

        const hasChildren = !!item.children?.length;

        if (item.link) {
            history.push(`/${item.link}`);
        }

        if (hasChildren) {
            setPanelOpen(true);
        } else {
            setPanelOpen(false);
        }
    };

    const activeTopItem = useMemo(
        () => topItems.find((t) => t.key === activeTopKey) || null,
        [topItems, activeTopKey]
    );

    if (!isUserLoaded) return <></>;

    return (
        <div className={classes.root}>
            <Drawer variant="permanent" className={classes.railDrawer} classes={{ paper: classes.railPaper }} PaperProps={{ ref: railRef }}>
                <Toolbar className={classes.railToolbar} disableGutters>
                    <img src="/images/ParqAIlogo.png" className={classes.railLogo} alt="parq.ai" />
                </Toolbar>

                <div className={classes.railScroll}>
                    <List className={classes.railList} disablePadding>
                        {topItems
                            .filter((t) => !t.checkVisibility || t.checkVisibility())
                            .map((t) => (
                                <Authorize key={t.key} profile={t.profile}>
                                    <TenantFeatureFlag featureFlagName={t.featureFlagName}>
                                        <RailItem
                                            item={t}
                                            active={t.key === activeTopKey}
                                            panelOpen={panelOpen}
                                            onClick={() => onRailClick(t)}
                                            onArrowClick={(item) => {
                                                setActiveTopKey(item.key);
                                                setPanelOpen((prev) => (activeTopKey === item.key ? !prev : true));
                                            }}
                                        />
                                    </TenantFeatureFlag>
                                </Authorize>
                            ))}
                    </List>
                </div>

                <div className={classes.railBottom}>
                    <div className={classes.railLogout}>
                        <Button className={classes.railLogoutBtn} onClick={logout} startIcon={<IconLogout />}>
                            Logout
                        </Button>
                    </div>
                </div>
            </Drawer>

            <Drawer
                variant="persistent"
                open={panelOpen && !!activeTopItem?.children?.length}
                className={classes.panelDrawer}
                classes={{ paper: classes.panelPaper }}
                anchor="left"
                PaperProps={{ ref: panelRef }}
            >
                <Toolbar className={classes.panelToolbar} disableGutters>
                    <div className={classes.panelHeader}>
                        <div className={classes.panelTitle}>
                            <span className={classes.panelTitleIcon}>{activeTopItem?.icon}</span>
                            <span className={classes.panelTitleText}>{activeTopItem?.text}</span>
                        </div>

                        <Button className={classes.panelCloseBtn} onClick={() => setPanelOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </Button>
                    </div>
                </Toolbar>

                <Divider />

                <div className={classes.panelScroll}>
                    <List className={classes.panelList} disablePadding>
                        {(activeTopItem?.children || [])
                            .filter((n) => !n.checkVisibility || n.checkVisibility())
                            .map((node) => (
                                <PanelNode
                                    key={node.key}
                                    node={node}
                                    level={2}
                                    isOpen={isOpen}
                                    toggleOpen={toggleOpen}
                                    onNavigate={go}
                                    currentPath={location.pathname}
                                    currentSearch={location.search}
                                />
                            ))}
                    </List>
                </div>
            </Drawer>
        </div>
    );
}

function RailItem({ item, active, panelOpen, onClick, onArrowClick }) {
    const classes = sideNavStyles();
    return (
        <div
            className={`${classes.railItem} ${active ? classes.railItemActive : ""}`}
            onClick={onClick}
            role="button"
            tabIndex={0}
            aria-label={item.text}
        >
            <div className={classes.railIcon}>{item.icon}</div>
            <div className={classes.railText}>
                {item.labelLines ? (
                    item.labelLines.map((line, i) => (
                        <div key={i} className={classes.railTextLine}>
                            {line}
                        </div>
                    ))
                ) : (
                    item.text
                )}

            </div>
            {item.children?.length ? (
                <div
                    className={`${classes.railArrow} ${active && panelOpen ? classes.railArrowOpen : ""}`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onArrowClick?.(item);
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <ChevronRightIcon fontSize="small" />
                </div>
            ) : null}
        </div>

    );
}
function PanelNode({ node, level, isOpen, toggleOpen, onNavigate, currentPath, currentSearch }) {
    const classes = sideNavStyles();
    const hasChildren = !!(node.children && node.children.length);
    const open = hasChildren ? isOpen(node.key) : false;

    const linkStr = node.link ? String(node.link) : "";
    const [linkPath, linkQuery] = linkStr.split("?");

    const isActive = (() => {
        if (!linkPath) return false;

        const pathMatch =
            currentPath === `/${linkPath}` ||
            currentPath.startsWith(`/${linkPath}/`);

        if (!pathMatch) return false;

        if (linkQuery) {
            const required = new URLSearchParams(linkQuery);
            const actual = new URLSearchParams(currentSearch || "");

            for (const [k, v] of required.entries()) {
                if (actual.get(k) !== v) return false;
            }
            return true;
        }

        if (currentSearch) return false;

        return true;
    })();
    return (
        <Authorize profile={node.profile}>
            <TenantFeatureFlag featureFlagName={node.featureFlagName}>
                <div className={classes.panelNodeWrap}>
                    <div
                        className={[
                            classes.panelRow,
                            level === 2 ? classes.panelRowL2 : classes.panelRowL3,
                            isActive ? classes.panelRowActive : "",
                        ].join(" ")}
                        onClick={() => {
                            if (node.link) {
                                onNavigate(node.link);
                                return;
                            }
                            if (hasChildren) {
                                toggleOpen(node.key);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <div className={classes.panelLeft}>
                            {node.icon ? <span className={classes.panelItemIcon}>{node.icon}</span> : null}
                            <span className={classes.panelItemText}>{node.text}</span>
                        </div>
                    </div>

                    {hasChildren ? (
                        <div className={classes.panelChildren}>
                            {node.children
                                .filter((c) => !c.checkVisibility || c.checkVisibility())
                                .map((child) => (
                                    <PanelNode
                                        key={child.key}
                                        node={child}
                                        level={3}
                                        isOpen={isOpen}
                                        toggleOpen={toggleOpen}
                                        onNavigate={onNavigate}
                                        currentPath={currentPath}
                                        currentSearch={currentSearch}
                                    />
                                ))}
                        </div>
                    ) : null}
                </div>
            </TenantFeatureFlag>
        </Authorize>
    );
}